import { v4 as uuidv4 } from 'uuid'
import Conf from './Conf'
import I18n from './I18n'
import Network from './Network'
import State from './State'
import Storage from './Storage'
import User from './User'

let windowId = null
let LivechatClient = null
let livechatInstance = null
let actualCloseCallback = null
let actualAgents = []
let eventBus = null

export default class Livechat {
  static setup(bus) {
    eventBus = bus
  }

  static acknowledgeMessage(messageId) {
    livechatInstance.acknowledgeMessage(messageId)
  }

  static on(name, cb) {
    eventBus.on(name, cb)
  }

  static off(name, cb) {
    eventBus.off(name, cb)
  }

  static emitTyping(content) {
    livechatInstance.emitTyping(content)
  }

  static reset() {
    if (livechatInstance) {
      livechatInstance.reset()
    }
  }

  static async agentsAvailable() {
    const ret = await Network.getInstance().get(
      `${Conf.get().services.livechat}/api/agents/count`,
      {
        available: 1,
        customerId: Conf.get().customerId,
        chatbotId: Conf.get().chatbotId,
        userId: User.id(),
        languages: [I18n.locale()],
        universes: [State.id() !== null ? await State.get('universeId', Conf.get().universe_id) : Conf.get().universe_id]
      }
    )
    return (ret.count ?? 0) > 0
  }

  static async listAgents() {
    return await Network.getInstance().get(
      `${Conf.get().services.livechat}/api/agents`,
      {
        customerId: Conf.get().customerId,
        chatbotId: Conf.get().chatbotId,
        languages: [I18n.locale()],
        universes: [State.id() !== null ? await State.get('universeId', Conf.get().universe_id) : Conf.get().universe_id]
      }
    )
  }

  static async sendMessage(text) {
    if (!livechatInstance || (text + '').trim().length === 0) {
      return
    }

    await livechatInstance.sendMessage(text)
  }

  static async init(userName, userEmail, closeCallback, handleTimeout = 5) {
    if (!windowId) {
      windowId = uuidv4()
    }

    if (LivechatClient === null) {
      const { default: livechatClientLib } = await import(/* webpackChunkName: "livechat" */ './LivechatClient')
      LivechatClient = livechatClientLib
    }

    let conversationId = await State.get('conversationId', null)
    if (conversationId === null) {
      await State.setMany({
        livechatStarted: false,
        livechatClosed: false
      })
    }

    if (livechatInstance) {
      livechatInstance.clean()
      livechatInstance = null
    }

    livechatInstance = new LivechatClient(
      Conf.get().customerId,
      User.id(),
      Conf.get().chatbotId,
      await Storage.get('session_id', null, true),
      eventBus,
      closeCallback,
      windowId
    )

    await livechatInstance.build(
      userName,
      userEmail,
      [I18n.locale()]
    )
    actualCloseCallback = closeCallback
    actualAgents = await this.listAgents()
    if (conversationId === null) {
      conversationId = await livechatInstance.createConversation(handleTimeout)
    }
    livechatInstance.setConversationId(conversationId)

    const conversation = await livechatInstance.getConversation()

    if (conversation.meta.closed) {
      actualCloseCallback()
    } else if (conversation.meta.agent) {
      livechatInstance.listenToChattingEvents()
    } else {
      // If the conversation is not handled yet
      await State.setMany({
        livechatStarted: false,
        livechatClosed: false
      })
      livechatInstance.listenForConversationHandled()
      livechatInstance.listenForConversationNotHandled()
    }

    return conversation
  }
}
